<template lang="pug">
  //- -------------------------------------------------------------------------
  //- NOTE: props.challenge needs to have the teams relation and team.isMember
  //- team.isAdmin attributes
  //- -------------------------------------------------------------------------
  div(v-if="!isAdmin && isTeamAdmin && !isJoined && allMemberRelatedTeams.length")
    WtJoinButton(
      teamJoinText="Add My Team"
      :key="allMemberRelatedTeams[0].id"
      :challenge="challenge" :team="allMemberRelatedTeams[0]"
      @isMemberParticipating="$event => $set(teamsJoinedStatus, allMemberRelatedTeams[0].id, $event)"
    )
  BDropdown(
    v-else
    aria-role="menu"
    :disabled="allMemberRelatedTeams.length < 1 || !(isTeamAdmin || isAdmin)"
    position="is-bottom-left"
    hoverable
    scrollable
    max-height="300px"
  )
    BTooltip.custom-tool-tip(
      :active="isToolipActive"
      multilined
      :label="tooltipText"
      :position="$route.matched[0].path === \"/challenges/:challengeId\" ? 'is-right' :  'is-top'"
      animated
      slot="trigger"
    )
      WtButton.wt-join-button(
        semibold
        :is-router-link="false"
        :is-text="true"
        :loading="!memberTeamsLoaded || !teamsMemberIsAdminLoaded"
      )
        span(:class="{'has-text-special-success': isJoined}") {{ joinWithText }}
        BIcon(
          v-if="isAdmin || isTeamAdmin || isJoined"
          :icon="isJoined ? 'check' : 'menu-down'"
          :class="isJoined ? 'has-text-special-success' : ''"
        )
    template(v-for="team in allMemberRelatedTeams")
      BDropdownItem(aria-role="menuitem" style="min-width: 300px" custom)
        article.media
          figure.media-left
            p.image.is-48x48
              img.is-rounded(
                :src="_.get(team, 'logo', 'https://walkertracker.s3.amazonaws.com/avatars/anonymous.png')"
                alt="avatar"
                @error="teamAvatarOnError"
              )
          .media-content
            .content
              .has-text-weight-semibold {{ team.name }}
              .is-5 {{ adminMemberText(team) }}
          .media-right
            WtJoinButton(:key="team.id" :challenge="challenge" :team="team" @isMemberParticipating="$event => $set(teamsJoinedStatus, team.id, $event)")
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import WtButton from "@@/WtButton/WtButton";
import WtJoinButton from "@@/WtJoinButton/WtJoinButton";

const STATIC = "static";
const API = "api";
const RECEIVER = "receiver";
export default {
  name: "JoinChallengeWithTeam",

  components: {
    WtButton,
    WtJoinButton
  },

  props: {
    challenge: {
      type: Object,
      default: null
    },
    /**
     * set as `api` to fetch teams to populate table, set as `receiver`
     * to allow teams to be added to table
     */
    mode: {
      type: String,
      default: "api",
      validator(mode) {
        return [STATIC, API, RECEIVER].indexOf(mode) !== -1;
      }
    }
  },

  data() {
    return {
      teamsJoinedStatus: {},
      teamsData: []
    };
  },
  computed: {
    ...mapGetters("authMember", ["isAdmin"]),
    ...mapGetters("members", [
      "memberTeams",
      "memberTeamsLoaded",
      "teamsMemberIsAdmin",
      "teamsMemberIsAdminLoaded"
    ]),
    ...mapGetters("challengeBuilder", ["stagedTeams"]),
    ...mapGetters("program", ["programTeams"]),

    allMemberRelatedTeams() {
      if (this.isAdmin) {
        return this.teamsData.map(teamData => ({ ...teamData, isAdmin: true }));
      }
      return this._.uniqBy(
        [
          ...this.challenge.teams.filter(team => team.isMember),
          ...this.teamsMemberIsAdmin
        ],
        "id"
      );
    },

    isJoined() {
      return this.allMemberRelatedTeams.some(
        team => this.teamsJoinedStatus[team.id]
      );
    },

    isTeamAdmin() {
      // is admin of at least one team
      if (this.isAdmin) {
        return true;
      }
      return this.teamsMemberIsAdmin.length > 0;
    },

    awaitingTeamLeaderToJoin() {
      return !this.isTeamAdmin && this.memberTeams.length > 0 && !this.isJoined;
    },

    noTeam() {
      return this.memberTeams.length < 1;
    },

    tooltipText() {
      if (
        !(this.isTeamAdmin || this.isAdmin) &&
        (this.noTeam || this.awaitingTeamLeaderToJoin)
      ) {
        return "If you are already on a team, ask your team lead to add your team to this challenge.  If you are not on a team yet, create your own (if applicable to your program),  or join a participating team!";
      }
      return "";
    },

    isToolipActive() {
      if (this.isAdmin) {
        return false;
      }
      return this.awaitingTeamLeaderToJoin || this.noTeam;
    },

    joinWithText() {
      if (this.isAdmin) {
        return "Add A Team";
      }
      if (!this.isJoined && this.isTeamAdmin) {
        return "Add A Team";
      }
      if (this.isJoined) {
        return "Joined"; // disabled with green-check
      }
      return "How to Join";
    }
  },

  async mounted() {
    if (this.isAdmin) {
      if (this.mode === STATIC) {
        await this.fetchProgramTeams({ all: 1 }).then(response => {
          this.teamsData = response.data;
        });
      } else if (this.mode === API) {
        await this.fetchProgramTeams();
        this.teamsData = this.programTeams;
      } else {
        this.teamsData = this.stagedTeams;
      }
    }

    if (!this.teamsMemberIsAdminLoaded) {
      this.fetchTeamsMemberIsAdmin(this.$auth.user().id);
    }
  },

  methods: {
    ...mapActions("members", ["fetchTeamsMemberIsAdmin"]),
    ...mapActions("program", ["fetchProgramTeams"]),

    adminMemberText(team) {
      // you're either a member or an admin or both but never neither in this list: allMemberRelatedTeams
      if (team.isAdmin && team.isMember) {
        return ""; // "* Team Leader And Participant In Challenges";
      }
      if (team.isAdmin && !team.isMember) {
        return ""; // "* Team Leader But Not Participant In Challenges";
      }
      if (!team.isAdmin && team.isMember) {
        return ""; // "* Team Member";
      }
      return "";
    },

    teamAvatarOnError(event) {
      event.target.onerror = null;
      event.target.src =
        "https://walkertracker.s3.amazonaws.com/avatars/anonymous.png";
    }
  }
};
</script>
<style lang="sass" scoped>
.dropdown.is-disabled
  opacity: 1
.dropdown.is-disabled /deep/ .dropdown-trigger
  pointer-events: all
.custom-tool-tip
  opacity: 1
.custom-tool-tip /deep/ .tooltip-content
  opacity: 1
</style>
